import { useCustomData, metaKey } from "@/lib/custom-data"
import { DotsSix, EllipsisHorizontal, Photo, Plus, Trash, XMark } from "@medusajs/icons"
import { Badge, Button, Checkbox, DropdownMenu, IconButton, Input, Label, Prompt, Select, Switch, Text, Textarea, toast } from "@medusajs/ui"
import { Suspense, useEffect, useMemo, useRef, useState } from "react"
import { lazyWithReload } from "@/lib/chunk-reload"
import { Link, useBlocker, useNavigate, useParams } from "react-router-dom"
import { Field } from "@/components/form"
import { FilePicker } from "@/components/file-picker"
import { RichTextEditor } from "@/components/rich-text"
import { ErrorState, PageHeader, SaveBar, Section, Status } from "@/components/ui"
import {
  DEFAULT_OPTION,
  duplicateProduct,
  emptyDraft,
  hasRealOptions,
  regenerateVariants,
  slugify,
  uploadFiles,
  useCollections,
  useDeleteProduct,
  useProductDraft,
  useProductOrder,
  useProductTypes,
  useSaveProduct,
  useStoreContext,
  validateDraft,
  variantTitle,
  type DraftOption,
  type DraftVariant,
  type ProductDraft,
} from "@/lib/catalog"
import { useCan } from "@/lib/permissions"
import { errorMessage } from "@/lib/sdk"
import { formatMoney } from "@/lib/format"
import { useMe } from "@/lib/api"
import { isAppOn } from "@/apps/registry"
import { productUrl } from "@/lib/storefront-url"

// Subscriptions app: the Purchase options card loads only when the app is on.
const PurchaseOptionsCard = lazyWithReload(() => import("@/apps/subscriptions/PurchaseOptions"))
const UpsellsCard = lazyWithReload(() => import("@/apps/upsells/ProductBlocks"))

/**
 * Shopify-style product page: one form, a contextual save bar, main column
 * (title/description, media, options & variants, SEO) and a side column
 * (status, organization). Used for both /products/new and /products/:id.
 */
export function ProductEditorPage() {
  const { id: routeId } = useParams()
  const isNew = !routeId || routeId === "new"
  const id = isNew ? null : routeId!
  const ctx = useStoreContext()
  const { data, error, isLoading } = useProductDraft(id, ctx.data?.locationId)
  const can = useCan()
  const canWrite = can("products:write")

  if (error) return <ErrorState error={error} />
  if (!isNew && (isLoading || !data)) return null
  return <Editor key={id ?? "new"} id={id} initial={data?.draft ?? null} productStatus={data?.product?.status} canWrite={canWrite} ctx={ctx.data} />
}

type Ctx = { salesChannelId?: string; locationId?: string; shippingProfileId?: string; locations: { id: string; name: string }[] } | undefined

function Editor({ id, initial, canWrite, ctx }: { id: string | null; initial: ProductDraft | null; productStatus?: string; canWrite: boolean; ctx: Ctx }) {
  const navigate = useNavigate()
  const base = useMemo(() => initial ?? emptyDraft(), [initial])
  const [d, setD] = useState<ProductDraft>(base)
  const [handleTouched, setHandleTouched] = useState(!!initial)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const save = useSaveProduct()
  const del = useDeleteProduct()
  const dirty = JSON.stringify(d) !== JSON.stringify(base)

  useEffect(() => setD(base), [base])

  // Shopify warns before leaving with unsaved changes.
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && !save.isPending && currentLocation.pathname !== nextLocation.pathname)
  useEffect(() => {
    if (!dirty) return
    const h = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener("beforeunload", h)
    return () => window.removeEventListener("beforeunload", h)
  }, [dirty])

  const set = <K extends keyof ProductDraft>(k: K, v: ProductDraft[K]) => setD((x) => ({ ...x, [k]: v }))

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const problem = validateDraft(d)
    if (problem) return toast.error(problem)
    save.mutate(
      { draft: d, existing: initial, id, ctx: { salesChannelId: ctx?.salesChannelId, locationId: ctx?.locationId, shippingProfileId: ctx?.shippingProfileId } },
      {
        onSuccess: (r) => {
          toast.success(id ? "Product saved" : "Product created")
          r.warnings.forEach((w) => toast.warning(w))
          if (!id) navigate(`/products/${r.id}`, { replace: true })
        },
        onError: (err) => toast.error(errorMessage(err)),
      }
    )
  }

  const onDuplicate = async () => {
    try {
      const r = await duplicateProduct(d, { salesChannelId: ctx?.salesChannelId, locationId: ctx?.locationId, shippingProfileId: ctx?.shippingProfileId })
      toast.success("Product duplicated as a draft")
      navigate(`/products/${r.id}`)
    } catch (err) {
      toast.error(errorMessage(err))
    }
  }

  const statusValue = d.archived ? "archived" : d.status === "published" ? "published" : "draft"

  return (
    <form onSubmit={onSubmit} className="pb-24">
      <PageHeader
        back="/products"
        title={
          <span className="flex items-center gap-x-2">
            {id ? base.title || "Untitled" : "Add product"} {id && <Status value={statusValue} />}
          </span>
        }
        actions={
          id && (
            <div className="flex items-center gap-2">
            {base.status === "published" && !base.archived && base.handle && (
              <Button type="button" variant="secondary" size="small" asChild>
                <a href={productUrl(base.handle)} target="_blank" rel="noreferrer">View</a>
              </Button>
            )}
            {canWrite && (
              <Button type="button" variant="secondary" size="small" onClick={onDuplicate}>
                Duplicate
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenu.Trigger asChild>
                <Button type="button" variant="secondary" size="small">
                  More actions <EllipsisHorizontal />
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="end">
                {canWrite && (
                  <DropdownMenu.Item onClick={() => set("archived", !d.archived)}>{d.archived ? "Unarchive" : "Archive product"}</DropdownMenu.Item>
                )}
                {canWrite && (
                  <>
                    <DropdownMenu.Separator />
                    <DropdownMenu.Item className="text-ui-fg-error" onClick={() => setConfirmDelete(true)}>
                      Delete product
                    </DropdownMenu.Item>
                  </>
                )}
              </DropdownMenu.Content>
            </DropdownMenu>
            <PrevNext id={id} />
            </div>
          )
        }
      />

      <fieldset disabled={!canWrite} className="grid gap-4 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
          <Section>
            <div className="flex flex-col gap-y-4 px-4 py-4">
              <Field label="Title">
                <Input
                  value={d.title}
                  placeholder="Short sleeve t-shirt"
                  onChange={(e) => {
                    const title = e.target.value
                    setD((x) => ({ ...x, title, handle: handleTouched ? x.handle : slugify(title) }))
                  }}
                />
              </Field>
              <Field label="Subtitle" hint="Optional line shown under the title on the product page.">
                <Input value={d.subtitle} onChange={(e) => set("subtitle", e.target.value)} />
              </Field>
              <div className="flex flex-col gap-y-1.5">
                <Text size="small" weight="plus">Description</Text>
                <RichTextEditor label="Description" value={d.description} onChange={(v) => set("description", v)} />
              </div>
            </div>
          </Section>

          <MediaSection d={d} setD={setD} />

          <Section title="Variants">
            <OptionsEditor d={d} setD={setD} />
            <VariantsTable d={d} setD={setD} locationName={ctx?.locations.find((l) => l.id === ctx.locationId)?.name} />
          </Section>

          <PurchaseOptions productId={id} canWrite={canWrite} />
          <UpsellsBlocks productId={id} canWrite={canWrite} />

          <Section title="Search engine listing">
            <div className="flex flex-col gap-y-4 px-4 py-4">
              <div className="rounded-md border bg-ui-bg-subtle p-3">
                <Text size="small" className="text-ui-fg-interactive">
                  {d.seo_title || d.title || "Product title"}
                </Text>
                <Text size="xsmall" className="text-ui-fg-muted">
                  /products/{d.handle || slugify(d.title) || "handle"}
                </Text>
                <Text size="xsmall" className="mt-1 text-ui-fg-subtle">
                  {d.seo_description || stripHtml(d.description).slice(0, 160) || "Add a description to see how this product might appear in search results."}
                </Text>
              </div>
              <Field label="Page title" hint={`${d.seo_title.length} of 70 characters used`}>
                <Input value={d.seo_title} maxLength={120} onChange={(e) => set("seo_title", e.target.value)} />
              </Field>
              <Field label="Meta description" hint={`${d.seo_description.length} of 160 characters used`}>
                <Textarea rows={3} value={d.seo_description} maxLength={320} onChange={(e) => set("seo_description", e.target.value)} />
              </Field>
              <Field label="URL handle">
                <Input
                  value={d.handle}
                  onChange={(e) => {
                    setHandleTouched(true)
                    set("handle", slugify(e.target.value) || e.target.value.toLowerCase())
                  }}
                />
              </Field>
            </div>
          </Section>
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          <Section>
            <div className="px-4 py-4">
              <Text size="small" weight="plus" className="mb-1.5">Status</Text>
              <Select
                value={statusValue}
                onValueChange={(v) => setD((x) => ({ ...x, archived: v === "archived", status: v === "published" ? "published" : "draft" }))}
              >
                <Select.Trigger>
                  <Select.Value />
                </Select.Trigger>
                <Select.Content>
                  <Select.Item value="published">Active</Select.Item>
                  <Select.Item value="draft">Draft</Select.Item>
                  <Select.Item value="archived">Archived</Select.Item>
                </Select.Content>
              </Select>
              <Text size="xsmall" className="mt-2 text-ui-fg-muted">
                {statusValue === "published" ? "Visible on your online store." : statusValue === "draft" ? "Hidden from your online store." : "Hidden and moved out of your active products."}
              </Text>
            </div>
          </Section>
          <OrganizationSection d={d} set={set} />
          <MetafieldsSection d={d} setD={setD} />
        </div>
      </fieldset>

      {canWrite && (
        <SaveBar
          visible={dirty || !id}
          message={id ? "Unsaved product changes" : "Unsaved product"}
          saving={save.isPending}
          onDiscard={() => (id ? setD(base) : navigate("/products"))}
        />
      )}

      <Prompt open={blocker.state === "blocked"}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Leave page with unsaved changes?</Prompt.Title>
            <Prompt.Description>Leaving this page will discard all unsaved changes.</Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel onClick={() => blocker.reset?.()}>Stay</Prompt.Cancel>
            <Prompt.Action onClick={() => blocker.proceed?.()}>Leave page</Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>

      <Prompt open={confirmDelete} onOpenChange={setConfirmDelete}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Delete {base.title}?</Prompt.Title>
            <Prompt.Description>This can't be undone. Orders that include this product keep their line items.</Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Cancel</Prompt.Cancel>
            <Prompt.Action
              onClick={() =>
                id &&
                del.mutate(id, {
                  onSuccess: () => {
                    toast.success("Product deleted")
                    setD(base)
                    navigate("/products", { replace: true })
                  },
                  onError: (err) => toast.error(errorMessage(err)),
                })
              }
            >
              Delete product
            </Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </form>
  )
}

// ── Pieces ─────────────────────────────────────────────────────────────────

const stripHtml = (s: string) => s.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()

type SetD = React.Dispatch<React.SetStateAction<ProductDraft>>

function MediaSection({ d, setD }: { d: ProductDraft; setD: SetD }) {
  const input = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [dragIdx, setDragIdx] = useState<number | null>(null)
  const [picking, setPicking] = useState(false)

  const add = async (files: File[]) => {
    const images = files.filter((f) => f.type.startsWith("image/") || f.type.startsWith("video/"))
    if (!images.length) return
    setUploading(true)
    try {
      const urls = await uploadFiles(images)
      setD((x) => ({ ...x, images: [...x.images, ...urls.map((url) => ({ url }))], thumbnail: x.thumbnail || urls[0] }))
    } catch (err) {
      toast.error(errorMessage(err))
    } finally {
      setUploading(false)
    }
  }

  const move = (from: number, to: number) =>
    setD((x) => {
      const images = [...x.images]
      const [m] = images.splice(from, 1)
      images.splice(to, 0, m)
      return { ...x, images, thumbnail: images[0]?.url ?? "" }
    })

  return (
    <Section title="Media">
      <div
        className="p-4"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault()
          if (e.dataTransfer.files.length) add(Array.from(e.dataTransfer.files))
        }}
      >
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
          {d.images.map((img, i) => (
            <div
              key={img.url + i}
              draggable
              onDragStart={() => setDragIdx(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                if (dragIdx == null || e.dataTransfer.files.length) return
                e.stopPropagation()
                move(dragIdx, i)
                setDragIdx(null)
              }}
              className={"group relative aspect-square overflow-hidden rounded-md border bg-ui-bg-component " + (i === 0 ? "col-span-2 row-span-2" : "")}
            >
              <img src={img.url} alt="" className="size-full object-cover" />
              <div className="absolute inset-x-1 top-1 flex justify-between opacity-0 transition group-hover:opacity-100">
                <span className="cursor-grab rounded bg-ui-bg-base/90 p-0.5 text-ui-fg-muted">
                  <DotsSix />
                </span>
                <IconButton
                  type="button"
                  size="2xsmall"
                  aria-label="Remove image"
                  onClick={() => setD((x) => ({ ...x, images: x.images.filter((_, j) => j !== i), thumbnail: x.thumbnail === img.url ? "" : x.thumbnail }))}
                >
                  <XMark />
                </IconButton>
              </div>
              {i === 0 && (
                <Badge size="2xsmall" className="absolute bottom-1 left-1">
                  Featured
                </Badge>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => input.current?.click()}
            className={
              "flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-ui-border-strong text-ui-fg-muted hover:bg-ui-bg-base-hover " +
              (d.images.length ? "aspect-square" : "col-span-full min-h-[140px]")
            }
          >
            <Photo />
            <Text size="xsmall" weight="plus">{uploading ? "Uploading…" : d.images.length ? "Add" : "Upload new"}</Text>
            {!d.images.length && <Text size="xsmall">Accepts images and videos — or drop files here</Text>}
          </button>
        </div>
        <div className="mt-2"><Button type="button" size="small" variant="secondary" onClick={() => setPicking(true)}>Select existing</Button></div>
        {picking && (
          <FilePicker
            multiple
            title="Select images"
            onClose={() => setPicking(false)}
            onDone={(fs) => {
              setPicking(false)
              const urls = fs.map((f) => f.url).filter((u) => !d.images.some((i) => i.url === u))
              setD((x) => ({ ...x, images: [...x.images, ...urls.map((url) => ({ url }))], thumbnail: x.thumbnail || urls[0] }))
            }}
          />
        )}
        {d.images.length > 1 && (
          <Text size="xsmall" className="mt-2 text-ui-fg-muted">
            Drag to reorder. The first image is the featured image.
          </Text>
        )}
        <input
          ref={input}
          type="file"
          accept="image/*,video/*"
          multiple
          hidden
          onChange={(e) => {
            add(Array.from(e.target.files ?? []))
            e.target.value = ""
          }}
        />
      </div>
    </Section>
  )
}

function OptionsEditor({ d, setD }: { d: ProductDraft; setD: SetD }) {
  const real = hasRealOptions(d)
  const options = real ? d.options : []

  const apply = (next: DraftOption[]) =>
    setD((x) => {
      // Keep the hidden default option's id so the first real option reuses it.
      const opts = next
      return { ...x, options: opts, variants: regenerateVariants(opts, x.variants) }
    })

  const update = (i: number, patch: Partial<DraftOption>) => {
    const next = options.map((o, j) => (j === i ? { ...o, ...patch } : o))
    // Renaming an option: carry variant values over to the new name.
    if (patch.title !== undefined && patch.title !== options[i].title) {
      const old = options[i].title
      setD((x) => ({
        ...x,
        options: next,
        variants: x.variants.map((v) => {
          const o = { ...v.options }
          if (old in o) {
            o[patch.title!] = o[old]
            delete o[old]
          }
          return { ...v, options: o }
        }),
      }))
      return
    }
    apply(next)
  }

  if (!real && !d.options.some((o) => o.title !== DEFAULT_OPTION)) {
    return (
      <div className="px-4 py-4">
        <Button type="button" variant="transparent" size="small" onClick={() => apply([{ title: "Size", values: [] }])}>
          <Plus /> Add options like size or color
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col divide-y">
      {options.map((o, i) => (
        <div key={i} className="flex flex-col gap-y-2 px-4 py-4">
          <div className="flex items-end gap-x-2">
            <div className="flex-1">
              <Field label="Option name">
                <Input size="small" value={o.title} onChange={(e) => update(i, { title: e.target.value })} />
              </Field>
            </div>
            <IconButton type="button" size="small" variant="transparent" aria-label="Remove option" onClick={() => apply(options.filter((_, j) => j !== i))}>
              <Trash />
            </IconButton>
          </div>
          <Label size="xsmall" className="text-ui-fg-subtle">
            Option values
          </Label>
          <TagInput values={o.values} onChange={(values) => update(i, { values })} placeholder="Type a value and press Enter" />
        </div>
      ))}
      {options.length < 3 && (
        <div className="px-4 py-3">
          <Button type="button" variant="transparent" size="small" onClick={() => apply([...options, { title: "", values: [] }])}>
            <Plus /> Add another option
          </Button>
        </div>
      )}
    </div>
  )
}

function VariantsTable({ d, setD, locationName }: { d: ProductDraft; setD: SetD; locationName?: string }) {
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [bulkPrice, setBulkPrice] = useState("")
  const single = !hasRealOptions(d)
  const upd = (i: number, patch: Partial<DraftVariant>) => setD((x) => ({ ...x, variants: x.variants.map((v, j) => (j === i ? { ...v, ...patch } : v)) }))

  if (single) {
    const v = d.variants[0]
    if (!v) return null
    return <VariantFields v={v} onChange={(p) => upd(0, p)} locationName={locationName} />
  }

  const prices = d.variants.map((v) => Number(v.price)).filter((n) => !isNaN(n) && n > 0)
  const totalQty = d.variants.reduce((s, v) => s + (Number(v.quantity) || 0), 0)

  return (
    <div>
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 bg-ui-bg-subtle px-4 py-2">
          <Text size="small">{selected.size} selected</Text>
          <Input size="small" className="w-28" placeholder="Price" value={bulkPrice} onChange={(e) => setBulkPrice(e.target.value)} />
          <Button
            type="button"
            size="small"
            variant="secondary"
            onClick={() => {
              setD((x) => ({ ...x, variants: x.variants.map((v, j) => (selected.has(j) ? { ...v, price: bulkPrice } : v)) }))
              setBulkPrice("")
            }}
          >
            Set price
          </Button>
          <Button
            type="button"
            size="small"
            variant="secondary"
            onClick={() => {
              setD((x) => ({ ...x, variants: x.variants.filter((_, j) => !selected.has(j)) }))
              setSelected(new Set())
            }}
          >
            Delete variants
          </Button>
        </div>
      )}
      {/* Phones: one block per variant */}
      <ul className="divide-y md:hidden">
        {d.variants.map((v, i) => (
          <li key={v.id ?? variantTitle(v, d)} className="flex flex-col gap-y-2 px-4 py-3">
            <label className="flex items-center gap-x-2">
              <Checkbox
                checked={selected.has(i)}
                onCheckedChange={(c) =>
                  setSelected((s) => {
                    const n = new Set(s)
                    c ? n.add(i) : n.delete(i)
                    return n
                  })
                }
              />
              <Text size="small" weight="plus">{variantTitle(v, d)}</Text>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Price"><MoneyInput full value={v.price} onChange={(price) => upd(i, { price })} /></Field>
              <Field label="Compare-at"><MoneyInput full value={v.compare_at} onChange={(compare_at) => upd(i, { compare_at })} /></Field>
              <Field label="Cost per item"><MoneyInput full value={v.cost} onChange={(cost) => upd(i, { cost })} /></Field>
              <Field label="SKU"><Input size="small" value={v.sku} onChange={(e) => upd(i, { sku: e.target.value })} /></Field>
              <Field label="Available">
                {v.manage_inventory ? (
                  <Input size="small" inputMode="numeric" value={v.quantity} onChange={(e) => upd(i, { quantity: e.target.value })} />
                ) : (
                  <Text size="small" className="text-ui-fg-muted">Not tracked</Text>
                )}
              </Field>
            </div>
          </li>
        ))}
      </ul>
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-left">
          <thead className="border-b text-ui-fg-subtle">
            <tr className="txt-compact-small-plus">
              <th className="w-10 py-2">
                <Checkbox
                  checked={selected.size === d.variants.length ? true : selected.size ? "indeterminate" : false}
                  onCheckedChange={(c) => setSelected(c ? new Set(d.variants.map((_, i) => i)) : new Set())}
                />
              </th>
              <th className="py-2">Variant</th>
              <th className="py-2">Price</th>
              <th className="py-2">Compare-at</th>
              <th className="py-2">Cost</th>
              <th className="py-2">SKU</th>
              <th className="py-2 pr-6">Available</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {d.variants.map((v, i) => (
              <tr key={v.id ?? variantTitle(v, d)}>
                <td className="py-2">
                  <Checkbox
                    checked={selected.has(i)}
                    onCheckedChange={(c) =>
                      setSelected((s) => {
                        const n = new Set(s)
                        c ? n.add(i) : n.delete(i)
                        return n
                      })
                    }
                  />
                </td>
                <td className="py-2 pr-2">
                  <Text size="small" weight="plus">
                    {variantTitle(v, d)}
                  </Text>
                </td>
                <td className="py-2 pr-2">
                  <MoneyInput value={v.price} onChange={(price) => upd(i, { price })} />
                </td>
                <td className="py-2 pr-2">
                  <MoneyInput value={v.compare_at} onChange={(compare_at) => upd(i, { compare_at })} />
                </td>
                <td className="py-2 pr-2">
                  <MoneyInput value={v.cost} onChange={(cost) => upd(i, { cost })} />
                </td>
                <td className="py-2 pr-2">
                  <Input size="small" className="w-28" value={v.sku} onChange={(e) => upd(i, { sku: e.target.value })} />
                </td>
                <td className="py-2 pr-6">
                  {v.manage_inventory ? (
                    <Input size="small" className="w-20" inputMode="numeric" value={v.quantity} onChange={(e) => upd(i, { quantity: e.target.value })} />
                  ) : (
                    <Text size="small" className="text-ui-fg-muted">
                      Not tracked
                    </Text>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-3">
        <Text size="xsmall" className="text-ui-fg-muted">
          {d.variants.length} variants
          {prices.length ? ` · ${formatMoney(Math.min(...prices))}${Math.max(...prices) !== Math.min(...prices) ? ` – ${formatMoney(Math.max(...prices))}` : ""}` : ""} · {totalQty} available
          {locationName ? ` at ${locationName}` : ""}
        </Text>
      </div>
    </div>
  )
}

/** Shopify's cost, profit and margin row (profit reports use the cost; customers never see it). */
function CostFields({ v, onChange }: { v: DraftVariant; onChange: (p: Partial<DraftVariant>) => void }) {
  const price = Number(v.price)
  const cost = Number(v.cost)
  const has = v.cost.trim() !== "" && !isNaN(cost) && v.price.trim() !== "" && !isNaN(price)
  const profit = has ? price - cost : null
  const margin = has && price > 0 ? Math.round(((price - cost) / price) * 1000) / 10 : null
  return (
    <div className="grid gap-4 px-4 py-4 sm:grid-cols-3">
      <Field label="Cost per item" hint="Customers won't see this.">
        <MoneyInput full value={v.cost} onChange={(cost) => onChange({ cost })} />
      </Field>
      <Field label="Profit">
        <Text size="small" className="pt-1.5">{profit == null ? "--" : `$${profit.toFixed(2)}`}</Text>
      </Field>
      <Field label="Margin">
        <Text size="small" className="pt-1.5">{margin == null ? "--" : `${margin}%`}</Text>
      </Field>
    </div>
  )
}

function VariantFields({ v, onChange, locationName }: { v: DraftVariant; onChange: (p: Partial<DraftVariant>) => void; locationName?: string }) {
  return (
    <div className="flex flex-col divide-y">
      <div className="grid gap-4 px-4 py-4 sm:grid-cols-3">
        <Field label="Price">
          <MoneyInput full value={v.price} onChange={(price) => onChange({ price })} />
        </Field>
        <Field label="Compare-at price" hint="Shown struck through.">
          <MoneyInput full value={v.compare_at} onChange={(compare_at) => onChange({ compare_at })} />
        </Field>
        <Field label="Weight (g)">
          <Input size="small" inputMode="decimal" value={v.weight} onChange={(e) => onChange({ weight: e.target.value })} />
        </Field>
      </div>
      <CostFields v={v} onChange={onChange} />
      <div className="flex flex-col gap-4 px-4 py-4">
        <Label size="small" weight="plus">
          Inventory
        </Label>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="SKU (Stock Keeping Unit)">
            <Input size="small" value={v.sku} onChange={(e) => onChange({ sku: e.target.value })} />
          </Field>
          <Field label="Barcode (ISBN, UPC, GTIN, etc.)">
            <Input size="small" value={v.barcode} onChange={(e) => onChange({ barcode: e.target.value })} />
          </Field>
        </div>
        <label className="flex items-center gap-x-2">
          <Switch checked={v.manage_inventory} onCheckedChange={(c) => onChange({ manage_inventory: c })} />
          <Text size="small">Track quantity</Text>
        </label>
        {v.manage_inventory && (
          <>
            <div className="max-w-[200px]">
              <Field label={locationName ? `Quantity at ${locationName}` : "Quantity"}>
                <Input size="small" inputMode="numeric" value={v.quantity} onChange={(e) => onChange({ quantity: e.target.value })} />
              </Field>
            </div>
            <label className="flex items-center gap-x-2">
              <Checkbox checked={v.allow_backorder} onCheckedChange={(c) => onChange({ allow_backorder: c === true })} />
              <Text size="small">Continue selling when out of stock</Text>
            </label>
          </>
        )}
      </div>
    </div>
  )
}

function MoneyInput({ value, onChange, full, id, ...aria }: { value: string; onChange: (v: string) => void; full?: boolean; id?: string; "aria-describedby"?: string }) {
  return (
    <div className={"relative " + (full ? "w-full" : "w-28")}>
      <span className="pointer-events-none absolute left-2 top-1/2 z-10 -translate-y-1/2 text-ui-fg-muted txt-compact-small">$</span>
      <Input id={id} aria-describedby={aria["aria-describedby"]} size="small" inputMode="decimal" className="pl-5" placeholder="0.00" value={value} onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ""))} />
    </div>
  )
}

function TagInput({ values, onChange, placeholder, suggestions }: { values: string[]; onChange: (v: string[]) => void; placeholder?: string; suggestions?: string[] }) {
  const [text, setText] = useState("")
  const commit = () => {
    const parts = text
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .filter((s) => !values.some((v) => v.toLowerCase() === s.toLowerCase()))
    if (parts.length) onChange([...values, ...parts])
    setText("")
  }
  const listId = useMemo(() => "dl-" + Math.random().toString(36).slice(2), [])
  return (
    <div className="flex flex-col gap-y-2">
      <Input
        size="small"
        value={text}
        list={suggestions ? listId : undefined}
        placeholder={placeholder}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault()
            commit()
          } else if (e.key === "Backspace" && !text && values.length) onChange(values.slice(0, -1))
        }}
      />
      {suggestions && (
        <datalist id={listId}>
          {suggestions.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      )}
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {values.map((v) => (
            <Badge key={v} size="xsmall" className="gap-x-1">
              {v}
              <button type="button" aria-label={`Remove ${v}`} onClick={() => onChange(values.filter((x) => x !== v))}>
                <XMark className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}

function OrganizationSection({ d, set }: { d: ProductDraft; set: <K extends keyof ProductDraft>(k: K, v: ProductDraft[K]) => void }) {
  const collections = useCollections()
  const types = useProductTypes()
  return (
    <Section title="Product organization">
      <div className="flex flex-col gap-y-4 px-4 py-4">
        <Field label="Type">
          <TypeInput value={d.type} onChange={(v) => set("type", v)} options={types.data?.product_types.map((t: any) => t.value) ?? []} />
        </Field>
        <Field label="Vendor">
          <Input size="small" value={d.vendor} onChange={(e) => set("vendor", e.target.value)} />
        </Field>
        <Field label="Collection">
          <Select value={d.collection_id || "none"} onValueChange={(v) => set("collection_id", v === "none" ? "" : v)}>
            <Select.Trigger>
              <Select.Value placeholder="None" />
            </Select.Trigger>
            <Select.Content>
              <Select.Item value="none">None</Select.Item>
              {collections.data?.collections.map((c: any) => (
                <Select.Item key={c.id} value={c.id}>
                  {c.title}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
        </Field>
        <Field label="Tags">
          <TagInput values={d.tags} onChange={(v) => set("tags", v)} placeholder="Add tags" />
        </Field>
      </div>
    </Section>
  )
}

function TypeInput({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: string[] }) {
  const id = useMemo(() => "types-" + Math.random().toString(36).slice(2), [])
  return (
    <>
      <Input size="small" list={id} value={value} placeholder="e.g. Shirts" onChange={(e) => onChange(e.target.value)} />
      <datalist id={id}>
        {options.map((o) => (
          <option key={o} value={o} />
        ))}
      </datalist>
    </>
  )
}

function MetafieldsSection({ d, setD }: { d: ProductDraft; setD: SetD }) {
  const defs = useCustomData().data?.definitions ?? []
  // Like Shopify: the card is always there; with no definitions it points to where they're made.
  if (!defs.length)
    return (
      <Section title="Metafields">
        <div className="px-4 py-4">
          <Text size="small" className="text-ui-fg-subtle">
            Add custom fields to products (ingredients, care instructions, specifications…) in{" "}
            <Link to="/settings/custom-data" className="text-ui-fg-interactive">Settings → Metafields</Link>. They then appear here on every product.
          </Text>
        </div>
      </Section>
    )
  const val = (k: string) => d.extra_metadata[metaKey(k)]
  const put = (k: string, v: unknown) =>
    setD((x) => {
      const m = { ...x.extra_metadata }
      if (v === "" || v == null) delete m[metaKey(k)]
      else m[metaKey(k)] = v
      return { ...x, extra_metadata: m }
    })
  return (
    <Section title="Metafields">
      <div className="flex flex-col gap-y-4 px-4 py-4">
        {defs.map((f) => {
          const v = val(f.key)
          const s = typeof v === "string" || typeof v === "number" ? String(v) : ""
          return (
            <Field key={f.id} label={f.name} hint={f.description || undefined}>
              {f.type === "multiline" ? <Textarea rows={3} value={s} maxLength={5000} onChange={(e) => put(f.key, e.target.value)} />
                : f.type === "boolean" ? <label className="flex items-center gap-2 text-[13px]"><input type="checkbox" checked={v === true} onChange={(e) => put(f.key, e.target.checked ? true : null)} />Yes</label>
                : f.type === "integer" ? <Input size="small" type="number" step={1} value={s} onChange={(e) => put(f.key, e.target.value === "" ? null : Math.trunc(Number(e.target.value)))} />
                : f.type === "decimal" ? <Input size="small" type="number" step="any" value={s} onChange={(e) => put(f.key, e.target.value === "" ? null : Number(e.target.value))} />
                : f.type === "date" ? <Input size="small" type="date" value={s} onChange={(e) => put(f.key, e.target.value)} />
                : f.type === "color" ? <Input size="small" placeholder="#aabbcc" maxLength={7} value={s} aria-invalid={!!s && !/^#[0-9a-fA-F]{6}$/.test(s)} onChange={(e) => put(f.key, e.target.value.trim())} />
                : f.type === "url" ? <Input size="small" placeholder="https://…" maxLength={500} value={s} aria-invalid={!!s && !/^https:\/\/\S+$/i.test(s)} onChange={(e) => put(f.key, e.target.value.trim())} />
                : <Input size="small" maxLength={500} value={s} onChange={(e) => put(f.key, e.target.value)} />}
            </Field>
          )
        })}
      </div>
    </Section>
  )
}

/** Shopify's Purchase options card (Subscriptions app): only while the app is on and the product exists. */
function PurchaseOptions({ productId, canWrite }: { productId: string | null; canWrite: boolean }) {
  const me = useMe()
  const can = useCan()
  if (!me.data || !isAppOn(me.data, "subscriptions") || !can("subscriptions:read")) return null
  if (!productId)
    return (
      <Section title="Purchase options">
        <Text size="small" className="px-4 py-4 text-ui-fg-subtle">Save the product first, then add subscription plans here.</Text>
      </Section>
    )
  return (
    <Suspense fallback={null}>
      <PurchaseOptionsCard productId={productId} canWrite={canWrite && can("subscriptions:write")} />
    </Suspense>
  )
}

/** Upsells app: the product's checkout image, checkout reviews and promo bar (only while the app is on). */
function UpsellsBlocks({ productId, canWrite }: { productId: string | null; canWrite: boolean }) {
  const me = useMe()
  const can = useCan()
  if (!productId || !me.data || !isAppOn(me.data, "upsells") || !can("upsells:read")) return null
  return (
    <Suspense fallback={null}>
      <UpsellsCard productId={productId} canWrite={canWrite && can("upsells:write")} />
    </Suspense>
  )
}

/** Shopify's ↑ ↓ arrows: step through products in list order without going back. */
function PrevNext({ id }: { id: string }) {
  const order = useProductOrder().data
  const navigate = useNavigate()
  const i = order ? order.indexOf(id) : -1
  if (i < 0 || order!.length < 2) return null
  const prev = i > 0 ? order![i - 1] : null
  const next = i < order!.length - 1 ? order![i + 1] : null
  const btn = (to: string | null, label: string, d: string) => (
    <button
      type="button"
      disabled={!to}
      onClick={() => to && navigate(`/products/${to}`)}
      aria-label={label}
      title={label}
      className="flex h-7 w-7 items-center justify-center rounded-[8px] text-[var(--tb-ink-2,#303030)] transition-colors hover:bg-black/5 disabled:opacity-30"
    >
      <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>
    </button>
  )
  return (
    <span className="flex items-center">
      {btn(prev, "Previous product", "M5 12.5 10 7.5l5 5")}
      {btn(next, "Next product", "M5 7.5l5 5 5-5")}
    </span>
  )
}
